import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { checkAuthOnLoad } from './store/slices/authSlice';
import AuthRedirectWrapper from './components/AuthRedirectWrapper';
import NotificationPopUp from './components/NotificationPopUp';
import GlobalOrderDetailModal from './components/GlobalOrderDetailModal';
import NotificationListener from './components/NotificationListener';
import PushNotificationRegistration from './components/PushNotificationRegistration';
import AndroidBackButtonHandler from './components/AndroidBackButtonHandler';
import AppActivityLogger from './components/AppActivityLogger';
import Login from './components/Login';
import Register from './components/Register';
import CustomerLogin from './components/CustomerLogin';
import PasswordReset from './components/PasswordReset';
import ForgotPassword from './components/ForgotPassword';
import ResetPasswordWithToken from './components/ResetPasswordWithToken';
import CustomerDashboard from './pages/customer/CustomerDashboard';
import GuestMenu from './pages/customer/GuestMenu';
import DineInReservation from './pages/customer/DineInReservation';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './components/Home';
import ContactUs from './pages/ContactUs';
import RestaurantsPage from './pages/RestaurantsPage';
import RestroLitePage from './pages/RestroLitePage';
import LiteOAuthCallbackPage from './pages/LiteOAuthCallbackPage';
import LitePublicBillPage from './pages/LitePublicBillPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import PocketBillingAIPrivacyPolicy from './pages/PocketBillingAIPrivacyPolicy';
import PocketBillingAITerms from './pages/PocketBillingAITerms';
import PocketBillingAiDownloadPage from './pages/PocketBillingAiDownloadPage';
import Blog from './pages/Blog';
import Careers from './pages/Careers';
import ContactLeadsList from './pages/superAdmin/ContactLeadsList';
import RestroLiteAdminPage from './pages/superAdmin/RestroLiteAdminPage';
import RestroLiteLeadsPage from './pages/superAdmin/RestroLiteLeadsPage';
import EmployeesManagementPage from './pages/superAdmin/EmployeesManagementPage';
import EmployeePerformancePage from './pages/superAdmin/EmployeePerformancePage';
import EmployeeDashboard from './pages/employee/EmployeeDashboard';
import EmployeeRestroLiteShopsPage from './pages/employee/EmployeeRestroLiteShopsPage';
import EmployeeRestroLiteLeadsPage from './pages/employee/EmployeeRestroLiteLeadsPage';
import { ToastProvider } from './components/ToastProvider';
import { UserProvider } from './components/UserContext';
import RestaurantDashboard from './pages/restaurant/RestaurantDashboard';
import SuperAdminDashboard from './pages/superAdmin/superAdminDashboard';
import FullscreenInsights from './pages/restaurant/FullscreenInsights';
import FullscreenMenu from './pages/restaurant/FullscreenMenu';
import FullscreenAnalytics from './pages/restaurant/FullscreenAnalytics';
import FullscreenTables from './pages/restaurant/FullscreenTables';
import FullscreenCounterOrders from './pages/restaurant/FullscreenCounterOrders';
import FullscreenStaff from './pages/restaurant/FullscreenStaff';
import FullscreenOffers from './pages/restaurant/FullscreenOffers';
import FullscreenPayments from './pages/restaurant/FullscreenPayments';
import CounterOrderIsolated from './pages/restaurant/CounterOrderIsolated';
import WaiterDashboard from './pages/waiter/WaiterDashboard';
import CookDashboard from './pages/cook/CookDashboard';

// Redux-enabled App component
const ReduxApp = () => {
  useEffect(() => {
    // Check authentication on app load
    store.dispatch(checkAuthOnLoad());
  }, []);

  return (
    <Router>
      <AndroidBackButtonHandler />
      <AppActivityLogger />
      <NotificationListener />
      <PushNotificationRegistration />
      <div className="App">
        <NotificationPopUp />
        <GlobalOrderDetailModal />
        <Routes>
          <Route path="/" element={<AuthRedirectWrapper><Home /></AuthRedirectWrapper>} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/restaurants" element={<RestaurantsPage />} />
          <Route path="/pocketbillingai" element={<RestroLitePage />} />
          <Route path="/pocketbillingai/privacy-policy" element={<PocketBillingAIPrivacyPolicy />} />
          <Route path="/pocketbillingai/terms" element={<PocketBillingAITerms />} />
          <Route path="/pocketbillingai/download" element={<PocketBillingAiDownloadPage />} />
          <Route path="/restro-lite" element={<Navigate to="/pocketbillingai" replace />} />
          <Route path="/oauth/callback" element={<LiteOAuthCallbackPage />} />
          <Route path="/lite/bill/:token" element={<LitePublicBillPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/login" element={<AuthRedirectWrapper><Login /></AuthRedirectWrapper>} />
          <Route path="/register" element={<Register />} />
          <Route path="/customer-login" element={<CustomerLogin />} />
          <Route path="/reset-password" element={<PasswordReset />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password-token" element={<ResetPasswordWithToken />} />
          
          {/* Restaurant Routes — specific paths before /restaurant/:tab */}
          <Route
            path="/restaurant"
            element={
              <ProtectedRoute allowedRoles={['restaurantadmin', 'staff']}>
                <Navigate to="/restaurant/menu" replace />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/waiter" 
            element={
              <ProtectedRoute allowedRoles={['staff']}>
                <WaiterDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/chef" 
            element={
              <ProtectedRoute allowedRoles={['staff']}>
                <CookDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/restaurant/insights-fullscreen" 
            element={
              <ProtectedRoute allowedRoles={['restaurantadmin']}>
                <FullscreenInsights />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/restaurant/menu-fullscreen" 
            element={
              <ProtectedRoute allowedRoles={['restaurantadmin']}>
                <FullscreenMenu />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/restaurant/analytics-fullscreen" 
            element={
              <ProtectedRoute allowedRoles={['restaurantadmin']}>
                <FullscreenAnalytics />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/restaurant/tables-fullscreen" 
            element={
              <ProtectedRoute allowedRoles={['restaurantadmin']}>
                <FullscreenTables />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/restaurant/counter-fullscreen" 
            element={
              <ProtectedRoute allowedRoles={['restaurantadmin']}>
                <FullscreenCounterOrders />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/restaurant/staff-fullscreen" 
            element={
              <ProtectedRoute allowedRoles={['restaurantadmin']}>
                <FullscreenStaff />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/restaurant/offers-fullscreen" 
            element={
              <ProtectedRoute allowedRoles={['restaurantadmin']}>
                <FullscreenOffers />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/restaurant/payments-fullscreen" 
            element={
              <ProtectedRoute allowedRoles={['restaurantadmin']}>
                <FullscreenPayments />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/restaurant/:tab"
            element={
              <ProtectedRoute allowedRoles={['restaurantadmin', 'staff']}>
                <RestaurantDashboard />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/test-counter" 
            element={<CounterOrderIsolated />} 
          />
          
          {/* Super Admin Routes */}
          <Route 
            path="/superadmin" 
            element={
              <ProtectedRoute allowedRoles={['superadmin']}>
                <SuperAdminDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/superadmin/leads" 
            element={
              <ProtectedRoute allowedRoles={['superadmin']}>
                <ContactLeadsList />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/superadmin/restro-lite"
            element={
              <ProtectedRoute allowedRoles={['superadmin']}>
                <RestroLiteAdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/restro-lite-leads"
            element={
              <ProtectedRoute allowedRoles={['superadmin']}>
                <RestroLiteLeadsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/employees"
            element={
              <ProtectedRoute allowedRoles={['superadmin']}>
                <EmployeesManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/employee-performance"
            element={
              <ProtectedRoute allowedRoles={['superadmin']}>
                <EmployeePerformancePage />
              </ProtectedRoute>
            }
          />

          {/* Company employee portal */}
          <Route
            path="/employee/dashboard"
            element={
              <ProtectedRoute allowedRoles={['companyemployee']}>
                <EmployeeDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/restro-lite-shops"
            element={
              <ProtectedRoute allowedRoles={['companyemployee']}>
                <EmployeeRestroLiteShopsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/restro-lite-leads"
            element={
              <ProtectedRoute allowedRoles={['companyemployee']}>
                <EmployeeRestroLiteLeadsPage />
              </ProtectedRoute>
            }
          />

          {/* Customer Routes */}
          <Route
            path="/customer"
            element={
              <ProtectedRoute allowedRoles={['customer']}>
                <CustomerDashboard />
              </ProtectedRoute>
            }
          />
          
          {/* Guest Menu Route - No authentication required */}
          <Route
            path="/menu/:restaurantId/:tableId"
            element={<GuestMenu />}
          />
          {/* Browse restaurant menu (no table - e.g. from home search) */}
          <Route
            path="/menu/:restaurantId"
            element={<GuestMenu />}
          />
          {/* Dine-in table reservation (public) */}
          <Route
            path="/reserve/:restaurantId"
            element={<DineInReservation />}
          />
        </Routes>
      </div>
    </Router>
  );
};

// Main App with Redux Provider
function App() {
  return (
    <Provider store={store}>
      <UserProvider>
        <ToastProvider>
          <ReduxApp />
        </ToastProvider>
      </UserProvider>
    </Provider>
  );
}

export default App;
